import Router from 'next/router';
import {AbstractUrlService, TFillStrategy} from 'core/abstract/AbstractUrlService';
import {BaseUrlParam} from 'core/base/BaseUrlParam';
import {contextDecorator} from 'core/decorators';

export type TUrlParam = {
    id: string;
    value?: string;
};

export class BaseUrlService extends AbstractUrlService {
    protected paramsList: Record<string, null | string> = {};

    protected options: AbstractUrlService['options'];
    protected onParamsChange: AbstractUrlService['onParamsChange'];
    protected query: AbstractUrlService['query'];

    // не стандартные фильтры, например фильтр по магазину имеет другую форму записи
    public static readonly COMMON_PARAM_NAMES: Record<string, BaseUrlParam> = {
        ...AbstractUrlService.COMMON_PARAM_NAMES,
    };

    constructor(
        query: AbstractUrlService['query'],
        onParamsChange: AbstractUrlService['onParamsChange'],
        options: AbstractUrlService['options'],
    ) {
        super();

        this.options = options;
        this.onParamsChange = onParamsChange;
        this.query = query;
    }

    public async hydrate() {
        await this.onParamsChange(this.format());
    }

    @contextDecorator('client')
    public async addParam(...params: TUrlParam[]) {
        const routerParamObject: Record<string, string> = params.reduce<Record<string, string>>(
            (routerObject, {id, value = 'Y'}) => {
                this.paramsList[id] = value;

                return {
                    ...routerObject,
                    [id]: value,
                };
            },
            {},
        );

        await Router.push(
            {
                query: {
                    ...Router.query,
                    ...routerParamObject,
                },
            },
            undefined,
            {
                shallow: true,
            },
        );

        await this.onParamsChange(this.format());
    }

    @contextDecorator('client')
    public async removeParam(...params: TUrlParam[]) {
        params.forEach(({id}) => {
            this.paramsList[id] = null;
            delete Router.query[id];
        });

        await Router.push(
            {
                query: Router.query,
            },
            undefined,
            {shallow: true},
        );

        await this.onParamsChange(this.format());
    }

    protected format(): URLSearchParams {
        Object.keys(this.paramsList).forEach((appliedParamId) => {
            if (null === this.paramsList[appliedParamId]) {
                this.query.delete(appliedParamId);
                return;
            }

            this.query.set(appliedParamId, this.paramsList[appliedParamId]!);
        });

        return this.query;
    }

    protected fill(fields: BaseUrlParam[], strategy: TFillStrategy) {
        Object.keys(this.options.query).forEach((queryKey: string) => {
            if ('include' === strategy) {
                if (fields.map(String).includes(queryKey)) {
                    this.paramsList[queryKey] = this.options.query[queryKey] as string;
                }
            }

            if ('exclude' === strategy) {
                if (!fields.map(String).includes(queryKey)) {
                    this.paramsList[queryKey] = this.options.query[queryKey] as string;
                }
            }
        });
    }
}
